/**
 * Sidebar navigation component.
 *
 * All 9 navigation items with role-based visibility,
 * presence dropdown, user extension, active-page highlighting.
 */

import { auth } from '../auth';
import { api } from '../api';
import { presencePoller } from '../services/presence-poller';
import { registry } from '../roles/registry';

interface PresenceOption {
    value: string;
    icon: string;
    label: string;
}

const PRESENCE_OPTIONS: PresenceOption[] = [
    { value: 'available', icon: '&#x1F7E2;', label: 'Доступний' },
    { value: 'busy', icon: '&#x1F534;', label: 'Зайнятий' },
    { value: 'dnd', icon: '&#128308;', label: 'Не турбувати' },
    { value: 'away', icon: '&#x1F7E1;', label: 'Відійшов' },
    { value: 'lunch', icon: '&#x1F7E0;', label: 'Обід' },
    { value: 'business_trip', icon: '&#x1F7E3;', label: 'Відрядження' },
];

/**
 * Render the sidebar into the given element.
 *
 * @param el   — the #sidebar element
 * @param user — { name, username, extension, role, ... }
 */
export function renderSidebar(el: HTMLElement, user: any): void {
    // Check admin via user object directly (avoids ES module cache issues with auth.js)
    const isAdmin =
        user.role === 'admin' || user.roles?.includes('admin') || user.roles?.includes('pbx-admin') || auth.isAdmin();
    const initials = (user.name || 'U')
        .split(' ')
        .map((w: string) => w[0])
        .join('')
        .slice(0, 2);
    const roleLabel = isAdmin
        ? 'Admin'
        : user.roles?.includes('dept-head') || auth.isDeptHead()
          ? 'Нач. відділу'
          : 'Користувач';

    // Build nav HTML from role registry (only items for current role)
    // Wrap label in <span> so we can hide it via CSS in collapsed mode.
    // `title` attribute provides the hover-tooltip when collapsed.
    const navHtml = registry
        .getNavItems()
        .map((item: any) => {
            if (item.section) return `<div class="nav-section">${item.section}</div>`;
            return (
                `<a class="nav-item" data-page="${item.page}" href="${item.href}" title="${item.label}">` +
                `<span class="nav-icon">${item.icon}</span>` +
                `<span class="nav-label">${item.label}</span>` +
                `</a>`
            );
        })
        .join('');

    // Build presence options HTML
    const presenceHtml = PRESENCE_OPTIONS.map(
        (opt) => `<option value="${opt.value}">${opt.icon} ${opt.label}</option>`,
    ).join('');

    el.innerHTML = `
    <div class="sidebar">
        <div class="sidebar-logo">
            <div class="logo-icon">N</div>
            <div class="sidebar-brand"><div class="logo-title">NIR PBX</div><div class="logo-sub">sip.nir.gov.ua</div></div>
            <button class="sidebar-toggle" id="sidebar-toggle" title="Згорнути / розгорнути меню" aria-label="Toggle sidebar">&#9776;</button>
        </div>
        <nav class="sidebar-nav">
            ${navHtml}
        </nav>
        <div class="sidebar-presence">
            <select class="presence-select" id="my-presence">
                ${presenceHtml}
            </select>
        </div>
        <div class="sidebar-user">
            <div class="avatar" style="background:var(--color-accent);color:#1D1D1B;font-weight:700">${initials}</div>
            <div>
                <div class="user-name">${user.name.split(' ').slice(0, 2).join(' ')}</div>
                <div class="user-ext">ext. ${user.extension} — ${roleLabel}</div>
            </div>
        </div>
    </div>`;

    // ------ Active page highlighting ------
    const updateActive = (): void => {
        const hash = location.hash.replace('#', '') || '/';
        el.querySelectorAll('.nav-item').forEach((a) => {
            a.classList.toggle('active', (a as HTMLElement).dataset.page === hash);
        });
    };
    updateActive();
    window.addEventListener('hashchange', updateActive);

    // ------ Sidebar collapse/expand ------
    // Restore persisted collapsed state
    if (localStorage.getItem('sidebar-collapsed') === '1') {
        document.body.classList.add('sidebar-collapsed');
    }
    const toggleBtn = document.getElementById('sidebar-toggle');
    if (toggleBtn) {
        toggleBtn.addEventListener('click', () => {
            const collapsed = document.body.classList.toggle('sidebar-collapsed');
            localStorage.setItem('sidebar-collapsed', collapsed ? '1' : '0');
        });
    }

    // ------ Mobile drawer ------
    const overlay = document.getElementById('sidebar-overlay');
    const isMobile = (): boolean => window.innerWidth <= 768;

    const closeMobileMenu = (): void => {
        document.body.classList.remove('sidebar-mobile-open');
    };

    // Close drawer when clicking overlay
    if (overlay) {
        overlay.addEventListener('click', closeMobileMenu);
    }

    // Close drawer when clicking a nav item on mobile
    el.querySelectorAll('.nav-item').forEach((a) => {
        a.addEventListener('click', () => {
            if (isMobile()) closeMobileMenu();
        });
    });

    // Override sidebar toggle for mobile: open drawer instead of collapsing
    if (toggleBtn) {
        toggleBtn.addEventListener(
            'click',
            (e: Event) => {
                if (isMobile()) {
                    e.stopImmediatePropagation();
                    document.body.classList.toggle('sidebar-mobile-open');
                }
            },
            true,
        ); // capture phase to run before existing handler
    }

    // ------ Presence dropdown ------
    const presSelect = document.getElementById('my-presence') as HTMLSelectElement | null;
    if (presSelect) {
        // Set initial value from poller cache (if available)
        const cached = presencePoller.getState(user.extension);
        if (cached && cached !== 'offline') {
            presSelect.value = cached;
        }

        // Sync dropdown when poller reports changes for our extension
        window.addEventListener('presence-update', (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail.extension === String(user.extension)) {
                const st: string = detail.state;
                if (PRESENCE_OPTIONS.some((o) => o.value === st)) {
                    presSelect.value = st;
                }
            }
        });

        // Push changes to API when user picks a new state
        presSelect.addEventListener('change', async (e: Event) => {
            const newState = (e.target as HTMLSelectElement).value;
            try {
                await api.put(`/presence/${user.extension}?state=${encodeURIComponent(newState)}`);
            } catch (err: any) {
                console.warn('Failed to update presence:', (err instanceof Error ? err.message : String(err)));
            }
        });
    }
}
